/**
 * SourcesColumn - Left panel for managing knowledge sources
 *
 * New design features:
 * - Dark theme with glass morphism
 * - Active Sources section (selected documents)
 * - Excluded from Context section (deselected, grayed)
 * - Smart Suggestions section
 * - Floating prompt bar at bottom
 */

import { useState, useMemo } from 'react';
import { KnowledgeDocument } from '@deep-agents/shared-types';
import { SourceUploader } from './SourceUploader';
import { SourceItem } from './SourceItem';

// === ICONS ===
const SparklesIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1 3 1z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const BookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

const PaperclipIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ZapIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const LayoutIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

// === COMPONENT ===
interface SourcesColumnProps {
  spaceId: string;
  documents: KnowledgeDocument[];
  selectedDocuments: Record<string, boolean>;
  onSelectionChange: (documentId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  isLoading: boolean;
  onDocumentUploaded: () => void;
  /** Document ID to highlight (for citation click) */
  highlightedDocumentId?: string | null;
}

export function SourcesColumn({
  spaceId,
  documents,
  selectedDocuments,
  onSelectionChange,
  onSelectAll,
  isLoading,
  onDocumentUploaded,
  highlightedDocumentId,
}: SourcesColumnProps) {
  const [isUploaderOpen, setIsUploaderOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Separate documents into active (selected) and excluded (deselected)
  const { activeDocs, excludedDocs } = useMemo(() => {
    const active: KnowledgeDocument[] = [];
    const excluded: KnowledgeDocument[] = [];

    documents.forEach((doc) => {
      if (selectedDocuments[doc.id]) {
        active.push(doc);
      } else {
        excluded.push(doc);
      }
    });

    return { activeDocs: active, excludedDocs: excluded };
  }, [documents, selectedDocuments]);

  const activeCount = activeDocs.length;
  const excludedCount = excludedDocs.length;

  return (
    <div className="sources-column">
      {/* Header */}
      <div className="sources-header">
        <div>
          <h2 className="sources-title">Project Context</h2>
          <p className="sources-subtitle">
            {activeCount} Active • {excludedCount} Excluded
          </p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="select-all-btn"
            onClick={() => onSelectAll(activeCount !== documents.length)}
          >
            {activeCount === documents.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="sources-scroll-area">
        {/* ACTIVE SOURCES SECTION */}
        {activeDocs.length > 0 && (
          <div className="sources-section">
            <h3 className="section-label active">Active Sources</h3>
            <div className="sources-list">
              {activeDocs.map((doc) => (
                <SourceItem
                  key={doc.id}
                  document={doc}
                  isSelected={true}
                  onSelectionChange={(selected) => onSelectionChange(doc.id, selected)}
                  isHighlighted={highlightedDocumentId === doc.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* EXCLUDED SECTION */}
        {excludedDocs.length > 0 && (
          <div className="sources-section excluded-section">
            <h3 className="section-label excluded">Excluded from Context</h3>
            <div className="sources-list">
              {excludedDocs.map((doc) => (
                <SourceItem
                  key={doc.id}
                  document={doc}
                  isSelected={false}
                  onSelectionChange={(selected) => onSelectionChange(doc.id, selected)}
                  isHighlighted={highlightedDocumentId === doc.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* LOADING STATE */}
        {isLoading && documents.length === 0 && (
          <div className="loading-state">
            <div className="loading-spinner" />
            <span>Loading sources...</span>
          </div>
        )}

        {/* EMPTY STATE */}
        {!isLoading && documents.length === 0 && (
          <div className="empty-state">
            <p>No sources yet</p>
            <p className="empty-hint">Add documents, URLs, or paste text to get started</p>
          </div>
        )}

        {/* SMART SUGGESTIONS SECTION */}
        <div className="sources-section suggestions-section">
          <div className="section-label-row">
            <SparklesIcon />
            <h3 className="section-label suggestions">Extend Knowledge</h3>
          </div>
          <div className="suggestions-grid">
            <button className="suggestion-card primary">
              <SearchIcon />
              <p className="suggestion-title">Deep Search</p>
              <p className="suggestion-hint">Add related research</p>
            </button>
            <button className="suggestion-card">
              <BookIcon />
              <p className="suggestion-title">Connect Source</p>
              <p className="suggestion-hint">Link external docs</p>
            </button>
          </div>
        </div>
      </div>

      {/* FLOATING PROMPT BAR */}
      <div className="floating-bar-container">
        <div className="floating-bar">
          {/* Input Row */}
          <div className="input-row">
            <button
              type="button"
              className="attach-btn"
              onClick={() => setIsUploaderOpen(true)}
            >
              <PaperclipIcon />
            </button>
            <input
              type="text"
              placeholder="Add source or ask to search..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="source-input"
            />
            <button
              type="button"
              className="add-btn"
              onClick={() => setIsUploaderOpen(true)}
            >
              <PlusIcon />
            </button>
          </div>

          {/* Shortcut Row */}
          <div className="shortcut-row">
            <div className="shortcut-buttons">
              <button className="shortcut-btn primary">
                <ZapIcon />
                <span>Deep Search</span>
              </button>
              <button className="shortcut-btn">
                <LayoutIcon />
                <span>Templates</span>
              </button>
            </div>
            <span className="keyboard-hint">⌘ + K</span>
          </div>
        </div>
      </div>

      {/* Uploader Modal */}
      {isUploaderOpen && (
        <div className="uploader-overlay">
          <div className="uploader-modal">
            <SourceUploader
              spaceId={spaceId}
              onSuccess={() => {
                onDocumentUploaded();
                setIsUploaderOpen(false);
              }}
              onCancel={() => setIsUploaderOpen(false)}
            />
          </div>
        </div>
      )}

      <style>{`
        /* === BASE LAYOUT === */
        .sources-column {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: transparent;
          overflow: hidden;
        }

        /* === HEADER === */
        .sources-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          flex-shrink: 0;
        }

        .sources-title {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }

        .sources-subtitle {
          font-size: 10px;
          color: rgba(148, 163, 184, 0.5);
          margin: 3px 0 0 0;
        }

        .header-actions {
          display: flex;
          gap: 6px;
        }

        .select-all-btn {
          font-size: 10px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.06);
          padding: 5px 10px;
          border-radius: 6px;
          color: rgba(148, 163, 184, 0.7);
          cursor: pointer;
          transition: all 0.15s;
        }

        .select-all-btn:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.1);
          color: #fff;
        }

        /* === SCROLLABLE AREA === */
        .sources-scroll-area {
          flex: 1;
          overflow-y: auto;
          padding-bottom: 12px;
        }

        /* Scrollbar styling */
        .sources-scroll-area::-webkit-scrollbar {
          width: 5px;
        }
        .sources-scroll-area::-webkit-scrollbar-track {
          background: transparent;
        }
        .sources-scroll-area::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 3px;
        }
        .sources-scroll-area::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        /* === SECTIONS === */
        .sources-section {
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .section-label {
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin: 0 0 2px 0;
        }

        .section-label.active {
          color: rgba(99, 102, 241, 0.7);
        }

        .section-label.excluded {
          color: rgba(100, 116, 139, 0.5);
        }

        .section-label.suggestions {
          color: rgba(129, 140, 248, 0.8);
        }

        .section-label-row {
          display: flex;
          align-items: center;
          gap: 6px;
          color: rgba(129, 140, 248, 0.8);
        }

        .sources-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .excluded-section {
          padding-top: 24px;
        }

        .suggestions-section {
          padding-top: 24px;
          padding-bottom: 16px;
        }

        /* === SUGGESTIONS GRID === */
        .suggestions-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .suggestion-card {
          padding: 10px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.04);
          text-align: left;
          cursor: pointer;
          transition: all 0.15s;
          color: rgba(148, 163, 184, 0.7);
        }

        .suggestion-card:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(255, 255, 255, 0.1);
          color: rgba(226, 232, 240, 0.9);
        }

        .suggestion-card.primary {
          background: rgba(99, 102, 241, 0.04);
          border-color: rgba(99, 102, 241, 0.08);
        }

        .suggestion-card.primary:hover {
          background: rgba(99, 102, 241, 0.08);
          border-color: rgba(99, 102, 241, 0.2);
        }

        .suggestion-card svg {
          margin-bottom: 6px;
        }

        .suggestion-title {
          font-size: 11px;
          font-weight: 500;
          color: inherit;
          margin: 0;
        }

        .suggestion-hint {
          font-size: 9px;
          color: rgba(99, 102, 241, 0.5);
          margin: 3px 0 0 0;
        }

        .suggestion-card:not(.primary) .suggestion-hint {
          color: rgba(100, 116, 139, 0.5);
        }

        /* === LOADING & EMPTY STATES === */
        .loading-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px 24px;
          gap: 12px;
          color: rgba(148, 163, 184, 0.6);
          font-size: 13px;
        }

        .loading-spinner {
          width: 24px;
          height: 24px;
          border: 2px solid rgba(99, 102, 241, 0.2);
          border-top-color: rgba(99, 102, 241, 0.8);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-hint {
          font-size: 11px;
          color: rgba(100, 116, 139, 0.5);
          margin: 0;
        }

        /* === FLOATING BAR === */
        .floating-bar-container {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 16px;
          z-index: 40;
          background: #0A0A0C;
          border-top: 1px solid rgba(255, 255, 255, 0.06);
        }

        .floating-bar {
          background: #1a1a1f;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 12px;
          padding: 8px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .input-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 4px 12px;
        }

        .attach-btn {
          background: none;
          border: none;
          color: rgba(100, 116, 139, 0.6);
          cursor: pointer;
          padding: 4px;
          transition: color 0.15s;
        }

        .attach-btn:hover {
          color: #fff;
        }

        .source-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          font-size: 14px;
          color: #fff;
        }

        .source-input::placeholder {
          color: rgba(100, 116, 139, 0.4);
        }

        .add-btn {
          width: 32px;
          height: 32px;
          border-radius: 10px;
          background: #6366f1;
          border: none;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .add-btn:hover {
          background: #818cf8;
        }

        .shortcut-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 8px 4px;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .shortcut-buttons {
          display: flex;
          gap: 6px;
        }

        .shortcut-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.05);
          font-size: 10px;
          color: rgba(148, 163, 184, 0.8);
          cursor: pointer;
          transition: all 0.15s;
        }

        .shortcut-btn:hover {
          color: #fff;
        }

        .shortcut-btn.primary {
          background: rgba(99, 102, 241, 0.1);
          border-color: rgba(99, 102, 241, 0.2);
          color: rgba(129, 140, 248, 0.9);
        }

        .keyboard-hint {
          font-size: 9px;
          color: rgba(100, 116, 139, 0.4);
          font-family: monospace;
        }

        /* === UPLOADER MODAL === */
        .uploader-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
        }

        .uploader-modal {
          background: #1a1a1f;
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          padding: 24px;
          max-width: 400px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
        }
      `}</style>
    </div>
  );
}
